import { type EntityTabCache } from './workspaceEntityCacheActions';
import type { Backlink, CategoryCollection, CategoryField, EntityCategory, EntityDocument, EntityFieldHistoryEntry, EntitySummary, EntityTag, EntityTemplate, ExplorerNode, PrototypeBlock, ResolvedEntityFieldValues } from '../types/entities';
import type { CharacterHistoryEntry } from '../types/relationships';
import type { CharacterHistoryTimelineOptions, CharacterTimelineData, TimelineEra, TimelineOccurrence } from '../types/timeline';
import type { SceneConflict, SceneSegment, SceneSegmentField } from '../types/scenes';
import type { WorkspaceSummary } from '../types/workspace';

type WorkspaceSessionStore = {
  $reset(): void;
  workspace: WorkspaceSummary | null;
  templates: EntityTemplate[];
  categories: EntityCategory[];
  categoryCollections: CategoryCollection[];
  categoryFields: CategoryField[];
  entities: EntitySummary[];
  tags: EntityTag[];
  activeEntityTags: EntityTag[];
  explorerNodes: ExplorerNode[];
  prototypeBlocks: PrototypeBlock[];
  timelineOccurrences: TimelineOccurrence[];
  timelineEras: TimelineEra[];
  characterTimeline: CharacterTimelineData | null;
  characterHistory: CharacterHistoryEntry[];
  characterHistoryLoaded: boolean;
  characterHistoryTimelineOptions: CharacterHistoryTimelineOptions;
  entityFieldHistory: EntityFieldHistoryEntry[];
  globalTimeContext: { sceneId: string | null; segmentId: string | null };
  resolvedEntityFieldValues: ResolvedEntityFieldValues | null;
  activeEntity: EntityDocument | null;
  backlinks: Backlink[];
  sceneSegments: SceneSegment[];
  sceneSegmentFields: SceneSegmentField[];
  sceneConflicts: SceneConflict[];
  entityTabCache: Map<string, EntityTabCache>;
  invalidateEntityOpenRequests(): void;
  clearWorkspaceTimelineRequests(): void;
  initialize(workspaceId?: string): Promise<void>;
};

let sessionGeneration = 0;

export function getWorkspaceSessionGeneration() { return sessionGeneration; }

export function advanceWorkspaceSessionGeneration() { sessionGeneration += 1; }

export const workspaceSessionActions = {
  clearSession(this: WorkspaceSessionStore) {
    advanceWorkspaceSessionGeneration();
    this.invalidateEntityOpenRequests();
    this.clearWorkspaceTimelineRequests();
    this.$reset();
    try { localStorage.removeItem('chronis.active-workspace.v1'); } catch { /* Speicher ist optional. */ }
  },
  async switchWorkspace(this: WorkspaceSessionStore, workspaceId: string) {
    if (workspaceId === this.workspace?.id) return;
    advanceWorkspaceSessionGeneration();
    this.invalidateEntityOpenRequests();
    this.clearWorkspaceTimelineRequests();
    this.workspace = null;
    this.templates = [];
    this.categories = [];
    this.categoryCollections = [];
    this.categoryFields = [];
    this.entities = [];
    this.tags = [];
    this.activeEntityTags = [];
    this.explorerNodes = [];
    this.prototypeBlocks = [];
    this.timelineOccurrences = [];
    this.timelineEras = [];
    this.characterTimeline = null;
    this.characterHistory = [];
    this.characterHistoryLoaded = false;
    this.entityFieldHistory = [];
    this.globalTimeContext = { sceneId: null, segmentId: null };
    this.resolvedEntityFieldValues = null;
    this.activeEntity = null;
    this.backlinks = [];
    this.sceneSegments = [];
    this.sceneSegmentFields = [];
    this.sceneConflicts = [];
    this.entityTabCache.clear();
    await this.initialize(workspaceId);
  }
};
